type ValidationCallback = (success: boolean, message?: string, details?: Record<string, any>) => void;
type ErrorHandler = (errorType: string, defaultMessage: string, result: any) => string;

interface ValidationResult {
  status: "success" | "error";
  message?: string;
  error_type?: string;
  details?: Record<string, any>;
}

const getDefaultErrorMessage = (errorType: string, defaultMessage: string): string => {
  switch (errorType) {
    case "invalid_or_expired_token":
      return "Invalid or expired access token. Check that the account is still valid in Webex, and try logging in again.";
    case "not_webex_admin":
      return "This user doesn't have the required permissions in Webex. Please make sure your user has admin privileges.";
    case "token_not_found":
      return "No Webex OAuth token is configured. Please select an OAuth token.";
    case "not_configured":
      return "No scheduling provider is configured. Please set up a scheduling service.";
    case "invalid_credentials":
      return "The credentials for the scheduling provider are invalid.";
    case "configuration_error":
      return "There is a configuration error with the scheduling provider.";
    case "api_error":
      return "Failed to connect to the scheduling provider API.";
    case "connection_error":
      return "A connection error occurred. Please check your network and try again.";
    default:
      return defaultMessage;
  }
};

export const validateWebexConnection = async (
  organizationId: string | number,
  callback: ValidationCallback,
  customErrorHandler?: ErrorHandler
): Promise<void> => {
  try {
    const response = await fetch(`/${organizationId}/linked_accounts/validate_webex`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCSRFToken(),
      },
    });

    const result: ValidationResult = await response.json();

    if (result.status === "success") {
      callback(true, result.message);
    } else {
      let errorMessage = result.error_type || "Failed to validate Webex connection";

      if (customErrorHandler) {
        errorMessage = customErrorHandler(result.error_type || "", errorMessage, result);
      } else {
        errorMessage = getDefaultErrorMessage(result.error_type || "", errorMessage);
      }

      callback(false, errorMessage);
    }
  } catch (error) {
    console.error("Error validating Webex connection", error);
    callback(false, "Network error occurred");
  }
};

export const validateSchedulingConnection = async (
  organizationId: string | number,
  callback: ValidationCallback,
  customErrorHandler?: ErrorHandler,
  draft?: Record<string, any>
): Promise<void> => {
  try {
    const response = await fetch(`/${organizationId}/linked_accounts/validate_scheduling`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCSRFToken(),
      },
      // When a draft is provided, validate the unsaved form values
      // (validate-before-save) instead of the persisted account.
      body: draft ? JSON.stringify({ linked_account: draft }) : undefined,
    });

    const result: ValidationResult = await response.json();

    if (result.status === "success") {
      callback(true, result.message, result.details);
    } else {
      let errorMessage = result.error_type || "Failed to validate scheduling connection";

      if (customErrorHandler) {
        errorMessage = customErrorHandler(result.error_type || "", errorMessage, result);
      } else {
        errorMessage = getDefaultErrorMessage(result.error_type || "", errorMessage);
      }

      callback(false, errorMessage);
    }
  } catch (error) {
    console.error("Error validating scheduling connection", error);
    callback(false, "Network error occurred");
  }
};


const getCSRFToken = (): string => {
  const meta = document.querySelector("meta[name='csrf-token']");
  return meta ? (meta as HTMLMetaElement).content : "";
};
